import { SingleNews } from '@api/api/news';
import { useEffect, useRef, useState } from 'react';

import { formatPillDate } from '../../utils/utils';

const SCROLL_PADDING = 40;

interface UseNewsScrollProps {
  activeNewsList: SingleNews[];
  activeNewsListObj: Record<string, SingleNews[]>;
}

export const useNewsScroll = ({
  activeNewsList,
  activeNewsListObj,
}: UseNewsScrollProps) => {
  const newsListRef = useRef<HTMLDivElement>(null);
  const [isShowToTopPill, setIsShowToTopPill] = useState(false);
  const prevRefetchTimestamp = useRef(new Date());

  useEffect(() => {
    const newsListDom = newsListRef.current;

    const handleScroll = (e: Event) => {
      if (!activeNewsList.length) {
        return;
      }

      const scrollTop = (e.currentTarget as HTMLDivElement).scrollTop;
      const prevRefetchTime = prevRefetchTimestamp.current.getTime();
      const latestNewsTime = new Date(activeNewsList[0].published).getTime();

      setIsShowToTopPill(scrollTop > 0 && latestNewsTime > prevRefetchTime);
    };

    if (newsListDom) {
      newsListDom.addEventListener('scroll', handleScroll);
    }

    return () => {
      newsListDom?.removeEventListener('scroll', handleScroll);
    };
  }, [activeNewsList]);

  const scrollToTop = () => {
    prevRefetchTimestamp.current = new Date();
    setIsShowToTopPill(false);
    newsListRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToDate = (date: string) => {
    const listDom = newsListRef.current;
    const firstNews = activeNewsListObj[formatPillDate(new Date(date))]?.[0];
    const firstNewsDom =
      firstNews &&
      listDom?.querySelector(`[data-article-id="${firstNews.id}"]`);

    if (!listDom || !firstNewsDom) {
      return false;
    }

    listDom.scrollTo({
      top:
        firstNewsDom.getBoundingClientRect().top -
        listDom.getBoundingClientRect().top +
        listDom.scrollTop -
        SCROLL_PADDING,
      behavior: 'smooth',
    });
    return true;
  };

  return { newsListRef, isShowToTopPill, scrollToTop, scrollToDate };
};
